import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, fmtDate, relTime, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";
import { Msg, useFlash } from "../ui";
import { SendDialog } from "./Notifications";

/** Strengthen others (FR-TEAM-04..06): who clicked the team link, what they answered, who needs a follow-up. */

interface Row {
  id: string;
  userId: string;
  name: string;
  alias: string;
  phone: string | null;
  clickedAt: string | null;
  answer: "trying" | "arrived" | "no_contact" | "not_interested" | null;
  answeredAt: string | null;
  choice: "have_team" | "unsure" | null;
  text: string | null;
  nextCheckInAt: string | null;
  checkInCount: number;
  inTeam: boolean | null;
  due: boolean;
  unansweredDays: number;
  lost: boolean;
  state: string;
}
interface Data {
  people: Row[];
  counts: Record<"all" | "trying" | "arrived" | "no_contact" | "not_interested" | "none" | "due", number>;
  followUp: Row[];
  teamUrl: string | null;
}

const FILTERS = ["all", "trying", "arrived", "no_contact", "not_interested", "none"] as const;

export function AdminTeam() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const sq = scopeQuery(scope, "church");
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const [messageTo, setMessageTo] = useState<Row | null>(null);
  const [flash, run] = useFlash();
  const q = useQuery({ queryKey: ["admin", "team", scope.churchId], queryFn: () => api<Data>(`/admin/team${sq}`), refetchInterval: 60_000, enabled: !(scope.canPickScope && !scope.churchId) });
  const ask = useMutation({ mutationFn: (id: string) => api(`/admin/team/${id}/ask${sq}`, { method: "POST" }), onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "team"] }).then(() => run(async () => undefined, t("admin.team.asked"))) });
  const d = q.data;
  if (scope.canPickScope && !scope.churchId)
    return (
      <>
        <h1>{t("admin.team.title")}</h1>
        <p className="muted">{t("admin.fams.noChurch")}</p>
      </>
    );
  const rows = (d?.people ?? []).filter((p) => filter === "all" || (filter === "none" ? !p.answer : p.answer === filter));

  const answerLabel = (p: Row) => (p.answer ? t(`admin.team.filter.${p.answer}`) : t("admin.team.filter.none"));

  return (
    <>
      <h1>{t("admin.team.title")}</h1>
      <p className="muted">{t("admin.team.body")}</p>
      {d && !d.teamUrl ? (
        <p className="error">
          {t("admin.team.noUrl")} <Link to="/admin/settings">→</Link>
        </p>
      ) : null}
      <Msg msg={flash} />

      <div className="tiles">
        <Tile n={d?.counts.all} label={t("admin.team.kpiClicked")} />
        <Tile n={d?.counts.arrived} label={t("admin.team.kpiArrived")} />
        <Tile n={d?.followUp.length} label={t("admin.team.kpiFollowUp")} warn />
      </div>

      <div className="toolbar">
        <div className="chips">
          {FILTERS.map((f) => (
            <button key={f} type="button" className={`chip ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>
              {t(`admin.team.filter.${f}`)} <span className="muted">{d?.counts[f] ?? "–"}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        <table className="people">
          <thead>
            <tr>
              <th>{t("admin.team.col.name")}</th>
              <th>{t("admin.team.col.clicked")}</th>
              <th>{t("admin.team.col.answer")}</th>
              <th>{t("admin.team.col.next")}</th>
              <th>{t("admin.team.col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !q.isLoading ? (
              <tr>
                <td colSpan={5} className="muted">
                  {t("admin.team.empty")}
                </td>
              </tr>
            ) : null}
            {rows.map((p) => (
              <tr key={p.id} className={p.lost ? "dim" : ""}>
                <td>
                  <strong>{p.name}</strong>
                  {p.phone ? (
                    <div className="small">
                      <a href={`tel:${p.phone}`}>{p.phone}</a>
                    </div>
                  ) : null}
                </td>
                <td>{p.clickedAt ? relTime(p.clickedAt, locale) : "—"}</td>
                <td>
                  <span className={`pill ${p.answer === "arrived" ? "ok" : p.answer === "not_interested" ? "warn" : ""}`}>{answerLabel(p)}</span>
                  {p.answer === "not_interested" ? <div className="muted small">{t(`admin.team.choice.${p.choice ?? "none"}`)}{p.text ? ` · “${p.text}”` : ""}</div> : null}
                  {p.answeredAt ? <div className="muted small">{relTime(p.answeredAt, locale)}</div> : null}
                </td>
                <td>
                  {p.nextCheckInAt ? (
                    <>
                      {fmtDate(p.nextCheckInAt, locale)} {p.due ? <span className="pill warn">{t("admin.team.due")}</span> : null}
                      <div className="muted small">{p.checkInCount}/2</div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="nowrap">
                  {p.nextCheckInAt && p.checkInCount < 2 ? (
                    <button className="btn small ghost" onClick={() => ask.mutate(p.id)}>
                      {t("admin.team.ask")}
                    </button>
                  ) : null}{" "}
                  <button className="btn small ghost" onClick={() => setMessageTo(p)}>
                    {t("admin.team.message")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`panel ${d && d.followUp.length > 0 ? "warn" : ""}`}>
        <h2>{t("admin.team.followUp")}</h2>
        <p className="muted small">{t("admin.team.followUpBody")}</p>
        <div className="list">
          {d?.followUp.map((p) => (
            <div key={p.id} className="listrow">
              <div className="grow">
                <strong>{p.name}</strong>
                <div className="muted small">{p.answer === "not_interested" ? t("admin.team.reasonNotInterested", { choice: t(`admin.team.choice.${p.choice ?? "none"}`) }) : t("admin.team.reasonNoAnswer", { n: p.unansweredDays })}</div>
              </div>
              {p.phone ? (
                <a className="btn small ghost" href={`tel:${p.phone}`}>
                  {t("admin.rewards.call")}
                </a>
              ) : null}
              <button className="btn small" onClick={() => setMessageTo(p)}>
                {t("admin.team.message")}
              </button>
            </div>
          ))}
        </div>
      </div>
      {messageTo ? <SendDialog recipient={{ userId: messageTo.userId, name: messageTo.name }} onClose={() => setMessageTo(null)} /> : null}
    </>
  );
}

function Tile({ n, label, warn }: { n: number | undefined; label: string; warn?: boolean }) {
  return (
    <div className={`tile ${warn && n ? "warn" : ""}`}>
      <div className="l">{label}</div>
      <div className="n">{n ?? "–"}</div>
    </div>
  );
}
